import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/primitives";
import { STAGES } from "@/data/process";

const PRINCIPLES = [
  { title: "Nothing is hidden", desc: "Line-by-line costing, stage-wise reports and photographs, and you see what we see." },
  { title: "Nothing is skipped", desc: "Every approval, mock-up and quality check happens in its right order. Always." },
  { title: "Every decision is yours", desc: "You approve the design, the samples and the spend, and we make sure you're never deciding blind." },
];

export default function ApproachPage() {
  return (
    <main className="bg-paper pt-16" data-testid="approach-page">
      <PageHeader
        index="07"
        eyebrow="Our Approach"
        title={
          <>
            How we build:<br />
            <em className="italic">The ARA TURNKEY Way</em>
          </>
        }
        sub="A 6-stage process where nothing is hidden, nothing is skipped, and every decision is yours to make."
      />

      <section className="px-6 py-20 lg:px-12 lg:py-28" data-testid="approach-principles">
        <div className="mx-auto grid max-w-[1440px] gap-6 lg:grid-cols-3">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="group h-full border border-ink/15 p-8 transition-colors duration-500 hover:bg-navy-deep" data-testid={`approach-principle-${i + 1}`}>
                <span className="font-sans text-sm font-bold text-maroon">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-2xl text-ink transition-colors duration-500 group-hover:text-paper">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/65 transition-colors duration-500 group-hover:text-paper/80">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 pb-24 lg:px-12 lg:pb-36" data-testid="approach-stages">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-24">
          {STAGES.map((s, i) => (
            <div key={s.name} className="grid gap-10 lg:grid-cols-12" data-testid={`approach-stage-${i + 1}`}>
              <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-28">
                  <Reveal>
                    <span
                      className="select-none font-sans text-7xl font-bold leading-none lg:text-8xl"
                      style={{ WebkitTextStroke: "1.5px rgba(18,17,22,0.25)", color: "transparent" }}
                    >
                      0{i + 1}
                    </span>
                    <h2 className="mt-4 font-serif text-3xl leading-tight text-ink lg:text-4xl">{s.name}</h2>
                    <p className="mt-5 max-w-sm text-sm leading-relaxed text-ink/65">{s.desc}</p>
                  </Reveal>
                </div>
              </div>
              <div className="lg:col-span-7">
                <Reveal delay={0.1}>
                  <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-maroon">What happens</p>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/75">{s.detail}</p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-10 text-[10px] font-medium uppercase tracking-[0.25em] text-maroon">
                    What you sign off
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    {s.deliverables.map((d) => (
                      <span key={d} className="rounded-full border border-ink/20 px-4 py-2 text-xs text-ink/70">
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className="mt-12 h-px w-full bg-gradient-to-r from-maroon via-ink/20 to-transparent" />
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink px-6 py-20 lg:px-12" data-testid="approach-cta">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-serif text-3xl text-paper sm:text-4xl">
              Ready to build the <em className="italic">ARA TURNKEY</em> way?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="approach-contact-button"
              className="inline-block rounded-full bg-maroon px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-transform duration-300 hover:scale-[1.04]"
            >
              Start a Project
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
